import "@tractie/styles/bundle";
import { useCallback, useEffect, useRef } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { SWRConfig, type Middleware } from "swr";

import { SessionStatusProvider } from "./context/session";

import type { Route } from ".react-router/types/app/+types/root";

const laggy: Middleware = (useSWRNext) => {
  return (key, fetcher, config) => {
    // Use a ref to store previous returned data.
    const laggyDataRef = useRef<any>(undefined);

    // Actual SWR hook.
    const swr = useSWRNext(key, fetcher, config);

    useEffect(() => {
      // Update ref if data is not undefined.
      if (swr.data !== undefined) {
        laggyDataRef.current = swr.data;
      }
    }, [swr.data]);

    // Expose a method to clear the laggy data, if any.
    const resetLaggy = useCallback(() => {
      laggyDataRef.current = undefined;
    }, []);

    // Fallback to previous data if the current data is undefined.
    const dataOrLaggyData = swr.data ?? laggyDataRef.current;

    // Is it showing previous data?
    const isLagging =
      swr.data === undefined && laggyDataRef.current !== undefined;

    // Also add a `isLagging` field to SWR.
    return {
      ...swr,
      data: dataOrLaggyData,
      isLagging,
      resetLaggy,
    };
  };
};

export const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export function Layout({ children }: { readonly children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <title>iNSpect - Camera Inspecties</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />

        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/train-filled-light-16.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/train-filled-dark-16.png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/train-filled-light-16.png"
          media="(prefers-color-scheme: light)"
        />

        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/train-filled-light-32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/train-filled-dark-32.png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/train-filled-light-32.png"
          media="(prefers-color-scheme: light)"
        />

        <link
          rel="icon"
          type="image/png"
          sizes="96x96"
          href="/train-filled-light-96.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="96x96"
          href="/train-filled-dark-96.png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="96x96"
          href="/train-filled-light-96.png"
          media="(prefers-color-scheme: light)"
        />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <SWRConfig value={{ use: [laggy] }}>
      <SessionStatusProvider>
        <Outlet />
      </SessionStatusProvider>
    </SWRConfig>
  );
}

export function ErrorBoundary({ error }: Readonly<Route.ErrorBoundaryProps>) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;
  console.error("Error:", import.meta.env.DEV);
  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? error.statusText || details
        : "The requested page could not be found.";
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
